import { useState } from 'react';
import { Box, LinearProgress, styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { ThemeTypography } from './ThemeTypography';
import ThemeButton from './ThemeButton';
import { FortnoxImportPhases } from '../fortnox/FortnoxImportPhases';
import { FortnoxImportRun, LatestFortnoxImportRuns } from '../../models/FortnoxImportRun';
import { FortnoxIntegrationType } from '../../types/backendTypes/FortnoxIntegrationType';
import { fortnoxImportProgress, isFortnoxImportActive } from '../../utils/fortnoxImportRun';
import { CLOCK_TIME_FORMAT } from '../../utils/formatClockTime';

/** How long without any change before the loader says there is no progress. */
export const FORTNOX_IMPORT_IDLE_AFTER_MS = 2 * 60 * 1_000;

type Props = {
  runs: LatestFortnoxImportRuns;
  /** When each run last changed, by run id, from useFortnoxImports. */
  lastChangeAt: Record<string, number>;
  onResume: (integrationType: FortnoxIntegrationType) => void;
  onStop: (integrationType: FortnoxIntegrationType) => void;
};

const INTEGRATION_NAMES: Record<FortnoxIntegrationType, string> = {
  [FortnoxIntegrationType.Base]: 'Bas',
  [FortnoxIntegrationType.Salary]: 'Lön',
};

/**
 * Shown while a Fortnox import is active. The import runs in the backend, so the page can be
 * left or reloaded; the progress is read again when the app is opened.
 *
 * It must not claim progress that is not happening. A run whose job has died says so and offers
 * to resume, and a run that has not moved for a couple of minutes says since when, rather than
 * showing the same count as if it were working.
 */
export const FortnoxLoader = ({ runs, lastChangeAt, onResume, onStop }: Props) => {
  const active = Object.values(runs).filter(isFortnoxImportActive) as FortnoxImportRun[];
  const stalled = active.filter((run) => run.stalled);
  const working = active.filter((run) => !run.stalled);

  const { processed, total } = working.reduce(
    (sum, run) => {
      const progress = fortnoxImportProgress(run);
      return { processed: sum.processed + progress.processed, total: sum.total + progress.total };
    },
    { processed: 0, total: 0 }
  );
  const counting =
    working.length > 0 && working.every((run) => run.status === 'running') && total > 0;

  const idleSince = working
    .map((run) => lastChangeAt[run.id])
    .filter(
      (at): at is number => at !== undefined && Date.now() - at > FORTNOX_IMPORT_IDLE_AFTER_MS
    )
    .sort((a, b) => a - b)[0];

  // Closing hides the box only for what it shows now: a run that stalls or a new one that
  // starts changes this key, and the box comes back, so a stop that needs action is not missed.
  const shownKey = active.map((run) => `${run.id}:${run.stalled}`).join(',');
  const [closedKey, setClosedKey] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);

  if (closedKey === shownKey) {
    return null;
  }

  return (
    <FortnoxLoaderContainer role="status" aria-label="Import från Fortnox">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: '-6px -6px -8px 0' }}>
        <IconButton
          size="small"
          aria-label={minimized ? 'Visa mer' : 'Minimera'}
          onClick={() => setMinimized(!minimized)}
        >
          {minimized ? <AddIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" aria-label="Stäng" onClick={() => setClosedKey(shownKey)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {working.length > 0 && (
        <>
          {counting ? (
            <LinearProgress
              color="inherit"
              variant="determinate"
              value={(processed / total) * 100}
              aria-label="Import från Fortnox"
            />
          ) : (
            <LinearProgress color="inherit" aria-label="Import från Fortnox" />
          )}

          <FortnoxLoaderText>
            <ThemeTypography sx={{ margin: '0' }}>
              <strong>
                {counting
                  ? `Hämtar data från Fortnox: ${processed} av ${total}`
                  : total > 0
                    ? `Läser in listor från Fortnox: ${total} poster hittade hittills`
                    : 'Hämtar data från Fortnox'}
              </strong>
            </ThemeTypography>

            {!minimized && (
              <>
                <ThemeTypography sx={{ margin: '0' }}>
                  {idleSince
                    ? `Ingen framdrift sedan ${dayjs(idleSince).format(CLOCK_TIME_FORMAT)}. Importen kan fortfarande vänta på Fortnox.`
                    : 'Det kan ta en stund. Du kan fortsätta arbeta eller stänga sidan.'}
                </ThemeTypography>

                {working.map((run) => (
                  <FortnoxImportPhases key={`phases-${run.id}`} run={run} />
                ))}

                {working.map((run) => (
                  <ThemeButton
                    key={run.id}
                    size="small"
                    onClick={() => onStop(run.integrationType)}
                  >
                    {working.length > 1
                      ? `Stoppa hämtningen (${INTEGRATION_NAMES[run.integrationType]})`
                      : 'Stoppa hämtningen'}
                  </ThemeButton>
                ))}
              </>
            )}
          </FortnoxLoaderText>
        </>
      )}

      {stalled.map((run) => (
        <FortnoxLoaderText key={run.id}>
          <ThemeTypography sx={{ margin: '0' }}>
            <strong>Importen från Fortnox har stannat</strong>
          </ThemeTypography>
          <ThemeTypography sx={{ margin: '0' }}>
            {`${fortnoxImportProgress(run).processed} av ${fortnoxImportProgress(run).total} hann hämtas. Återuppta för att fortsätta där den slutade.`}
          </ThemeTypography>
          <ThemeButton size="small" onClick={() => onResume(run.integrationType)}>
            Återuppta hämtningen
          </ThemeButton>
        </FortnoxLoaderText>
      ))}
    </FortnoxLoaderContainer>
  );
};

const FortnoxLoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  position: 'fixed',
  top: '5px',
  right: '5px',
  width: '25%',
  zIndex: 9999,
  padding: '10px',
  background: theme.palette.background.default,
  borderRadius: '8px',
  color: theme.palette.customColors.emerald,
  boxShadow: 'inset 0px 0px 2px rgba(0,0,0,0.5)',
}));

const FortnoxLoaderText = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  color: theme.palette.text.primary,
}));
